import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, getMe, login as apiLogin, logout as apiLogout, getStoredToken } from '../services/authService';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAdmin: boolean;
  isReadonly: boolean;
  canWrite: boolean;
  canEditDominioMapping: boolean;
}

const AuthContext = createContext<AuthContextType>(null!);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    if (!getStoredToken()) {
      setLoading(false);
      return;
    }
    try {
      const me = await getMe();
      setUser(me);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email: string, password: string) => {
    const res = await apiLogin(email, password);
    setUser(res.user);
  };

  const logout = () => {
    setUser(null);
    apiLogout();
  };

  const isAdmin = user?.role === 'admin';
  const isReadonly = user?.role === 'readonly' || user?.role === 'contabilidade';
  const canWrite = user?.role === 'admin' || user?.role === 'editor';
  const canEditDominioMapping = canWrite || user?.role === 'contabilidade';

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isAdmin, isReadonly, canWrite, canEditDominioMapping }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
